import React from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import LineLuxuryEscapesMonogramStampIcon from 'LuxKit/Icons/line/LineLuxuryEscapesMonogramStampIcon'
import LineLuxuryEscapesMonogramFilledStampIcon from 'LuxKit/Icons/line/LineLuxuryEscapesMonogramFilledStampIcon'

const MAX_COLUMNS = 5

const StampGrid = styled.div<{ $gap: number; $columns: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $columns }) => $columns}, auto);
  align-items: center;
  gap: ${({ $gap }) => rem($gap)};
  justify-content: start;
`

interface Props {
  /** Number of filled (completed) stamps. Clamped to `[0, total]`. */
  count: number
  /** Total stamp slots rendered (filled + dashed). */
  total: number
  /** Width/height of each stamp in pixels. */
  size: number
  /** Gap between stamps in pixels. Defaults to 12. */
  gap?: number
  /** Colour for the dashed (uncompleted) stamps. Filled stamps keep their default dark circle. */
  dashedColour?: App.Design.ContentColour
  /** Opacity applied to the dashed (uncompleted) stamps. Omit to render at full opacity. */
  dashedOpacity?: number
}

/**
 * Renders a horizontal list of Luxury Escapes monogram stamps, used to visualise
 * progress toward a target (e.g. Société Gold qualification). `count` filled
 * stamps appear first, followed by the remaining dashed (faded) stamps.
 */
function LuxuryEscapesStampGrid({ count, total, size, gap = 12, dashedColour, dashedOpacity }: Props) {
  const filled = Math.min(Math.max(count, 0), total)
  const dashed = total - filled
  const dashedStyle = { ...(dashedOpacity !== undefined && { opacity: dashedOpacity }) }
  const maxColumnsPerRow = Math.min(MAX_COLUMNS, total)

  return <StampGrid $gap={gap} $columns={maxColumnsPerRow}>
    {Array.from({ length: filled }, (_, i) => (
      <LineLuxuryEscapesMonogramFilledStampIcon key={`filled-${i}`} size={size} />
    ))}
    {Array.from({ length: dashed }, (_, i) => (
      <LineLuxuryEscapesMonogramStampIcon key={`dashed-${i}`} size={size} colour={dashedColour} style={dashedStyle} />
    ))}
  </StampGrid>
}

export default LuxuryEscapesStampGrid
